import { AccountId } from "@hashgraph/sdk"; // --- ADD THIS IMPORT ---

// mirrorUtils.ts
const queue: (() => void)[] = [];
let activeRequests = 0;
const MAX_REQUESTS_PER_SECOND = 50;
const INTERVAL = 1000 / MAX_REQUESTS_PER_SECOND;

// --- ADD THIS CONSTANT ---
const MIRROR_NODE_URL = "https://mainnet.mirrornode.hedera.com";

// Simple rate limiter
const processQueue = () => {
  if (queue.length === 0 || activeRequests >= MAX_REQUESTS_PER_SECOND) return;

  const fn = queue.shift();
  if (fn) {
    activeRequests++;
    fn();
    setTimeout(() => {
      activeRequests--;
      processQueue();
    }, INTERVAL);
  }
};

export const limitedFetch = (
  input: RequestInfo,
  init?: RequestInit,
): Promise<Response> =>
  new Promise((resolve, reject) => {
    queue.push(() => {
      fetch(input, init).then(resolve).catch(reject);
    });
    processQueue();
  });

// Fetch all tokens with pagination
export const getAllAssociatedTokens = async (
  accountId: string,
): Promise<any[]> => {
  let tokens: any[] = [];
  // --- USE CONSTANT ---
  let nextLink: string | null = `${MIRROR_NODE_URL}/api/v1/accounts/${accountId}/tokens?limit=100`;

  while (nextLink) {
    //const res = await limitedFetch(nextLink);
    const res: Response = await limitedFetch(nextLink);

    const data = await res.json();
    tokens = tokens.concat(data.tokens || []);
    nextLink = data.links?.next
      ? `${MIRROR_NODE_URL}${data.links.next}` // --- USE CONSTANT ---
      : null;
  }

  return tokens;
};

// Check token association
export const isAssociatedWithToken = async (
  accountId: string,
  tokenId: string,
): Promise<boolean> => {
  const tokens = await getAllAssociatedTokens(accountId);
  return tokens.some((t: any) => t.token_id === tokenId);
};

// Get token balance
export const getTokenBalance = async (
  accountId: string,
  tokenId: string,
): Promise<number> => {
  const tokens = await getAllAssociatedTokens(accountId);
  const token = tokens.find((t: any) => t.token_id === tokenId);
  return token ? parseInt(token.balance) : 0;
};

// --- NEW FUNCTION TO CHECK ALLOWANCE ---
export const getTokenAllowance = async (
  ownerId: string,
  spenderId: string,
  tokenId: string,
): Promise<number> => {
  try {
    const url = `${MIRROR_NODE_URL}/api/v1/accounts/${ownerId}/allowances/tokens?spender.id=${spenderId}&token.id=${tokenId}`;
    const res = await limitedFetch(url);
    const data = await res.json();

    if (data && data.allowances && data.allowances.length > 0) {
      // Find the specific allowance
      const allowance = data.allowances.find(
        (a: any) =>
          a.spender === spenderId &&
          a.token_id === tokenId &&
          a.owner === ownerId,
      );
      return allowance ? Number(allowance.amount) : 0;
    }
    return 0;
  } catch (err) {
    console.error("Failed to fetch token allowance:", err);
    return 0;
  }
};

// --- ADDED THIS FUNCTION (from previous step) ---
// It uses the Mirror Node REST API to query a contract's read-only function
export const getDepositedBalance = async (
  contractId: string,
  userAccountId: AccountId,
): Promise<bigint> => {
  try {
    // The function signature for `getBalance(address)` is `0xf8b2cb4f`
    // The user's address must be 0x-prefixed and padded
    const userAddress = userAccountId.toSolidityAddress();
    const functionSelector = "0xf8b2cb4f";
    const data = `${functionSelector}${userAddress.slice(2).padStart(64, "0")}`;

    const payload = {
      to: AccountId.fromString(contractId).toSolidityAddress(),
      data: data,
      estimate: false,
    };

    const url = `${MIRROR_NODE_URL}/api/v1/contracts/call`;
    const res = await limitedFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const resultData = await res.json();
    if (resultData && resultData.result) {
      // The result is a hex string, convert it to BigInt
      return BigInt(resultData.result);
    }
    return BigInt(0);
  } catch (err) {
    console.error("Failed to query getBalance via mirror node:", err);
    return BigInt(0);
  }
};

